import React, { useEffect, useRef, useState } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { Container, Row, Col, Button, Spinner, Tabs, Tab, Card, OverlayTrigger, Tooltip } from 'react-bootstrap';
import Plot from 'react-plotly.js';
import Select from 'react-select';
import { FaCheckCircle, FaTimesCircle, FaStar } from 'react-icons/fa';
import ComparableProperties from '../components/ComparableProperties';
import RatingComponent from '../components/RatingComponent';
import SuburbCard from '../components/SuburbCard';
import { handleTokenAndPostRequest, setMetaTags, trackEvent } from '../utils/utils';
import './Styles.css';
import { useAgentDetails } from '../utils/useAgentDetails';

// Time-range options for the Suburb Property Type Summary. Values are in days
// and map to the time_range param accepted by the
// /suburb-property-type-last-month-summary route. Mirrors the options used in
// the ComparableProperties date-range selector.
const propertyTypeRangeOptions = [
  { value: '7', label: 'Last week' },
  { value: '30', label: 'Last month' },
  { value: '90', label: 'Last 3 months' },
  { value: '183', label: 'Last 6 months' },
  { value: '365', label: 'Last year' },
  { value: '730', label: 'Last 2 years' },
];

const SuburbProfile = () => {
  // Tracks the (agent_slug, suburb_id) we've already alerted for, so the
  // suburb_profile_view alert fires once per page visit instead of on every
  // re-render or dependency change.
  const alertSentForRef = useRef(null);
  const [suburbData, setSuburbData] = useState(null);
  const [nearbySuburbData, setNearbySuburbData] = useState(null);
  const [loadingNearby, setLoadingNearby] = useState(false);
  const [suburbMonthlyData, setSuburbMonthlyData] = useState(null);
  const [suburbPropertyTypeData, setSuburbPropertyTypeData] = useState(null);
  const [rentSuburbPropertyTypeData, setRentSuburbPropertyTypeData] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1200);
  console.log("isMobile", isMobile);
  const [soldSuburbPropertyTypeData, setSoldSuburbPropertyTypeData] = useState(null);
  // Default to "Last year" for the property type summary time range.
  const [propertyTypeRange, setPropertyTypeRange] = useState(propertyTypeRangeOptions[4]);
  const { suburb } = useParams();

  const { agent_slug, agentDetails, setAgentDetails, branding } = useAgentDetails();

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 1200);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);   


  useEffect(() => {
    const fetchNearby = async () => {
      if (!suburbData || !suburbData.nearby_suburbs) return;
      setLoadingNearby(true);
      try {
        const nearbySuburbIds = JSON.parse(suburbData.nearby_suburbs);
        const response = await handleTokenAndPostRequest(
          `${window.api_host}/suburb`,
          {
            suburb_ids: nearbySuburbIds,
            agent_slug: agent_slug,
            generate_llm: false
          },
          {
            'Content-Type': 'application/json',
          }
        );

        setNearbySuburbData((response.data));
      } catch (error) {
        setNearbySuburbData([]);
        console.error("Error fetching nearby suburb data", error);
      }
      setLoadingNearby(false);
    };

    fetchNearby();
  }, [suburbData, agent_slug]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await handleTokenAndPostRequest(
          `${window.api_host}/suburb`,
          {
            suburb_ids: [suburb], // send as array
            agent_slug: agent_slug,
          },
          {
            'Content-Type': 'application/json',
          }
        );
        // response.data is an array; take the first element
        setSuburbData(response.data && response.data.length > 0 ? response.data[0] : null);
      } catch (error) {
        console.error("Error fetching suburb data", error);
      }
    };
  
    fetchData();
  }, [suburb, agent_slug]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await handleTokenAndPostRequest(
          `${window.api_host}/suburb-property-type-last-month-summary`,
          {
            suburb: suburb,
            agent_slug: agent_slug,
            time_range: propertyTypeRange.value,
          },
          {
            'Content-Type': 'application/json',
          }
        );
        const data = typeof response.data === 'string' ? JSON.parse(response.data) : response.data;

        setSuburbPropertyTypeData(data);
        setSoldSuburbPropertyTypeData(data['Sold']);
        setRentSuburbPropertyTypeData(data['Rent']);
      } catch (error) {
        console.error("Error fetching suburb data", error);
      }
    };

    fetchData();
  }, [suburb, agent_slug, propertyTypeRange]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await handleTokenAndPostRequest(
          `${window.api_host}/suburb-monthly`,
          {
            suburb: suburb,
            agent_slug: agent_slug,
          },
          {
            'Content-Type': 'application/json',
          }
        );
        const data = typeof response.data === 'string' ? JSON.parse(response.data) : response.data;
        setSuburbMonthlyData(data);
      } catch (error) {
        console.error("Error fetching suburb data", error);
      }
    };

    fetchData();
  }, [suburb, agent_slug]);

  useEffect(() => {
    if (agentDetails && suburbData) {
      const title = `${suburbData.suburb_initcap} Real Estate Insights by ${agentDetails.agency_name_short}`;
      
      const description = `Explore in-depth real estate insights and market trends for ${suburbData.suburb_initcap}, ${suburbData.state} ${suburbData.postcode}. Discover detailed property values, recent sales data, rental market analysis, and comprehensive suburb reports. Stay informed with the latest updates on median prices, days on market, and property types. Brought to you by ${agentDetails.agency_name_short}, your trusted source for real estate information.`;
      
      const keywords = `Real Estate, ${suburbData.suburb_initcap} Properties, ${suburbData.state} Market Trends, Property Values, ${agentDetails.agency_name_short}, Suburb Reports, Median Prices, Rental Market, Sales Data, Property Types, Real Estate Insights, ${suburbData.postcode} Real Estate, ${suburbData.suburb_initcap} Housing Market, ${suburbData.state} Real Estate`;
  
      const url = `https://homeprice.site/suburb-profile/${suburb}`;
      const image = agentDetails.landing_background_url;
  
      setMetaTags({ title, description, keywords, url, image });
    }
  }, [agentDetails, suburbData, suburb]);

  // Fire a single agent alert + tracking event per suburb profile visit.
  // Runs after suburbData is loaded so we can include the suburb name/state
  // in the alert metadata. Guarded by alertSentForRef to avoid duplicate
  // sends across re-renders or React StrictMode double-invocations.
  useEffect(() => {
    if (!agentDetails || !suburbData || !agent_slug) return;
    const key = `${agent_slug}::${suburb}`;
    if (alertSentForRef.current === key) return;
    alertSentForRef.current = key;

    const suburbName = suburbData.suburb_initcap || suburbData.suburb || '';
    const meta = {
      suburb_id: suburb,
      suburb_name: suburbName,
      state: suburbData.state || '',
      postcode: suburbData.postcode || '',
    };

    trackEvent('view_suburb_profile', { agent_slug, ...meta });

    // Notify the agent. The backend only sends an email when the request has
    // an authenticated/param-token user, so anonymous visitors are silently
    // ignored — no need to gate on the FE.
    handleTokenAndPostRequest(
      `${window.api_host}/save-settings`,
      {
        agent_slug,
        event_type: 'suburb_profile_view',
        address_text: [suburbName, suburbData.state, suburbData.postcode].filter(Boolean).join(', '),
        metadata: meta,
      },
      { 'Content-Type': 'application/json' },
    ).catch((err) => {
      console.error('Error sending suburb_profile_view alert', err);
    });
  }, [agentDetails, suburbData, suburb, agent_slug]);

  if (!agentDetails || !suburbData || !suburbPropertyTypeData || !suburbMonthlyData) {
    return (
      <div style={{ 
        position: 'absolute', 
        top: '50%', 
        left: '50%', 
        transform: 'translate(-50%, -50%)' 
      }}>
        <Spinner animation="border" />
      </div>
    );
  }

  const {
    median_sold_days_on_market_filled,
    median_sold_price_filled,
    prior_median_sold_days_on_market,
    prior_median_sold_price,
    prior_sold_properties,
    median_sold_days_on_market_percent_change_text,
    median_sold_price_percent_change_text,
    sold_properties_percent_change_text,
    sold_properties,
    median_rent_days_on_market_filled,
    median_rent_price_filled,
    prior_median_rent_days_on_market,
    prior_median_rent_price,
    prior_rent_properties,
    median_rent_days_on_market_percent_change_text,
    median_rent_price_percent_change_text,
    rent_properties_percent_change_text,
    rent_properties,
    reporting_month,
    suburb_postcode,
    suburb_summary,
  } = suburbData;

  // Sold price display
  const median_sold_price_display =
    typeof median_sold_price_filled === 'number' && median_sold_price_filled !== null
      ? `$${median_sold_price_filled.toLocaleString('en-US')}`
      : 'N/A';

  // Sold days on market delta
  const sold_days_on_market_delta =
    typeof median_sold_days_on_market_filled === 'number' &&
    typeof prior_median_sold_days_on_market === 'number' &&
    prior_median_sold_days_on_market !== 0 &&
    median_sold_days_on_market_filled !== null &&
    prior_median_sold_days_on_market !== null
      ? ((median_sold_days_on_market_filled - prior_median_sold_days_on_market) / prior_median_sold_days_on_market * 100).toFixed(0)
      : null;

  // Sold price delta
  const sold_price_delta =
    typeof median_sold_price_filled === 'number' &&
    typeof prior_median_sold_price === 'number' &&
    prior_median_sold_price !== 0 &&
    median_sold_price_filled !== null &&
    prior_median_sold_price !== null
      ? ((median_sold_price_filled - prior_median_sold_price) / prior_median_sold_price * 100).toFixed(0)
      : null;

  // Sold properties delta
  const sold_properties_delta =
    typeof sold_properties === 'number' &&
    typeof prior_sold_properties === 'number' &&
    prior_sold_properties !== 0 &&
    sold_properties !== null &&
    prior_sold_properties !== null
      ? ((sold_properties - prior_sold_properties) / prior_sold_properties * 100).toFixed(0)
      : null;

  // Rent price display
  const median_rent_price_display =
    typeof median_rent_price_filled === 'number' && median_rent_price_filled !== null
      ? `$${median_rent_price_filled.toLocaleString('en-US')}`
      : 'N/A';

  // Rent days on market delta
  const rent_days_on_market_delta =
    typeof median_rent_days_on_market_filled === 'number' &&
    typeof prior_median_rent_days_on_market === 'number' &&
    prior_median_rent_days_on_market !== 0 &&
    median_rent_days_on_market_filled !== null &&
    prior_median_rent_days_on_market !== null
      ? ((median_rent_days_on_market_filled - prior_median_rent_days_on_market) / prior_median_rent_days_on_market * 100).toFixed(0)
      : null;

  // Rent price delta
  const rent_price_delta =
    typeof median_rent_price_filled === 'number' &&
    typeof prior_median_rent_price === 'number' &&
    prior_median_rent_price !== 0 &&
    median_rent_price_filled !== null &&
    prior_median_rent_price !== null
      ? ((median_rent_price_filled - prior_median_rent_price) / prior_median_rent_price * 100).toFixed(0)
      : null;

  // Rent properties delta
  const rent_properties_delta =
    typeof rent_properties === 'number' &&
    typeof prior_rent_properties === 'number' &&
    prior_rent_properties !== 0 &&
    rent_properties !== null &&
    prior_rent_properties !== null
      ? ((rent_properties - prior_rent_properties) / prior_rent_properties * 100).toFixed(0)
      : null;

  const formatDataForPlotlyBar = (data, column_to_plot) => {
    if (!data) {
      return [];
    }
    const propertyTypes = [...new Set(data.map(item => item.property_type_display))];
    return propertyTypes.map(propertyType => {
      const filteredData = data.filter(item => item.property_type_display === propertyType);
      return {
        x: filteredData.map(item => item.sold_month),
        y: filteredData.map(item => item[column_to_plot]),
        type: 'bar',
        name: propertyType
      };
    });
  };

  const formatDataForPlotly = (data, column_to_plot) => {
    if (!data) {
      return [];
    }
    const propertyTypes = [...new Set(data.map(item => item.property_type_display))];
    return propertyTypes.map(propertyType => {
      const filteredData = data.filter(item => item.property_type_display === propertyType);
      return {
        x: filteredData.map(item => item.sold_month),
        y: filteredData.map(item => item[column_to_plot]),
        type: 'scatter',
        mode: 'lines+markers',
        name: propertyType
      };
    });
  };

  const SuburbsList = ({ suburbs, agent_slug }) => {
    if (!suburbs || !suburbs.length) {
      return <div>No nearby suburb data found.</div>;
    }
    // Only show first 3 on mobile
    const displaySuburbs = isMobile ? suburbs.slice(0, 3) : suburbs;
  
    return (
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
          gap: 16,
          marginTop: 16,
        }}
      >
        {displaySuburbs.map((suburb, idx) => (
          <SuburbCard key={suburb.suburb_id || idx} suburb={suburb} agent_slug={agent_slug} />
        ))}
      </div>
    );
  };

  const accent = branding.accentColor;

  return (
    <div>
      {agentDetails && suburbData && (
        <div
          id='suburb-profile-hero'
          className="sp-hero"
        >
          <div className="sp-hero-overlay" />
          <div className="sp-hero-content">
            <span className="sp-hero-badge" style={{ backgroundColor: accent }}>{suburbData.state}</span>
            <h1 className="sp-hero-title">{suburbData.suburb_initcap}</h1>
            <div className="sp-hero-meta">
              <span className="sp-hero-postcode">{suburbData.postcode}</span>
              <span className="sp-hero-divider">·</span>
              <span className="sp-hero-region">{suburbData.state}, Australia</span>
            </div>
          </div>
        </div>
      )}
      <Container style={{ paddingTop: '48px', maxWidth: '800px', paddingBottom: '60px' }}>
      {suburbData && suburb_summary && (() => {
          // Parse JSON string fields if needed
          let ratings = null;
          let ratingExplanations = null;
          let upsides = [];
          let downsides = [];

          try {
            ratings = suburbData.ratings ? JSON.parse(suburbData.ratings) : null;
          } catch (e) {
            ratings = null;
          }
          try {
            ratingExplanations = suburbData.rating_explanations ? JSON.parse(suburbData.rating_explanations) : null;
          } catch (e) {
            ratingExplanations = null;
          }
          try {
            upsides = suburbData.upsides ? JSON.parse(suburbData.upsides) : [];
          } catch (e) {
            upsides = [];
          }
          try {
            downsides = suburbData.downsides ? JSON.parse(suburbData.downsides) : [];
          } catch (e) {
            downsides = [];
          }

          return (
            <div className="sp-overview-section">
              <h2 className="sp-section-title">Suburb Overview</h2>
              <p className="sp-overview-text">{suburb_summary}</p>
              {ratings && ratingExplanations && (
                <RatingComponent
                  data={ratings}
                  explanations={ratingExplanations}
                />
              )}
              <h5 className="sp-sub-heading">Upsides</h5>
              <ul className="sp-pros-cons-list">
                {Array.isArray(upsides) && upsides.length > 0 ? (
                  upsides.map((upside, index) => {
                    const [title, ...description] = upside.split(':');
                    return (
                      <li key={index}>
                        <FaCheckCircle className="sp-icon-green" />
                        <span><strong>{title}:</strong>{description.join(':')}</span>
                      </li>
                    );
                  })
                ) : (
                  <li>No upsides available.</li>
                )}
              </ul>
              <h5 className="sp-sub-heading">Downsides</h5>
              <ul className="sp-pros-cons-list">
                {Array.isArray(downsides) && downsides.length > 0 ? (
                  downsides.map((downside, index) => {
                    const [title, ...description] = downside.split(':');
                    return (
                      <li key={index}>
                        <FaTimesCircle className="sp-icon-red" />
                        <span><strong>{title}:</strong>{description.join(':')}</span>
                      </li>
                    );
                  })
                ) : (
                  <li>No downsides available.</li>
                )}
              </ul>
            </div>
          );
        })()}
        <h2 className="sp-section-title">Summary Last Month</h2>
        <h5 className="sp-sub-heading" style={{ marginBottom: 16 }}>Sale Market</h5>
        <Row className="g-3 mb-4">
          <Col xs={12} md={4}>
            <div className="sp-stat-card">
              <span className="sp-stat-label">Median Days on Market</span>
              <span className="sp-stat-value">{median_sold_days_on_market_filled}</span>
              <span className={`sp-stat-delta ${median_sold_days_on_market_percent_change_text === '0%' ? 'neutral' : sold_days_on_market_delta > 0 ? 'positive' : 'negative'}`}>
                {median_sold_days_on_market_percent_change_text}
              </span>
            </div>
          </Col>
          <Col xs={12} md={4}>
            <div className="sp-stat-card">
              <span className="sp-stat-label">Sold Properties</span>
              <span className="sp-stat-value">{sold_properties}</span>
              <span className={`sp-stat-delta ${sold_properties_percent_change_text === '0%' ? 'neutral' : sold_properties_delta > 0 ? 'positive' : 'negative'}`}>
                {sold_properties_percent_change_text}
              </span>
            </div>
          </Col>
          <Col xs={12} md={4}>
            <div className="sp-stat-card">
              <span className="sp-stat-label">Median Sold Price</span>
              <span className="sp-stat-value">{median_sold_price_display}</span>
              <span className={`sp-stat-delta ${median_sold_price_percent_change_text === '0%' ? 'neutral' : sold_price_delta > 0 ? 'positive' : 'negative'}`}>
                {median_sold_price_percent_change_text}
              </span>
            </div>
          </Col>
        </Row>
        <h5 className="sp-sub-heading" style={{ marginBottom: 16 }}>Rental Market</h5>
        <Row className="g-3 mb-4">
          <Col xs={12} md={4}>
            <div className="sp-stat-card">
              <span className="sp-stat-label">Median Days on Market</span>
              <span className="sp-stat-value">{median_rent_days_on_market_filled}</span>
              <span className={`sp-stat-delta ${median_rent_days_on_market_percent_change_text === '0%' ? 'neutral' : rent_days_on_market_delta > 0 ? 'positive' : 'negative'}`}>
                {median_rent_days_on_market_percent_change_text}
              </span>
            </div>
          </Col>
          <Col xs={12} md={4}>
            <div className="sp-stat-card">
              <span className="sp-stat-label">Rented Properties</span>
              <span className="sp-stat-value">{rent_properties}</span>
              <span className={`sp-stat-delta ${rent_properties_percent_change_text === '0%' ? 'neutral' : rent_properties_delta > 0 ? 'positive' : 'negative'}`}>
                {rent_properties_percent_change_text}
              </span>
            </div>
          </Col>
          <Col xs={12} md={4}>
            <div className="sp-stat-card">
              <span className="sp-stat-label">Median Rent Price</span>
              <span className="sp-stat-value">{median_rent_price_display}</span>
              <span className={`sp-stat-delta ${median_rent_price_percent_change_text === '0%' ? 'neutral' : rent_price_delta > 0 ? 'positive' : 'negative'}`}>
                {median_rent_price_percent_change_text}
              </span>
            </div>
          </Col>
        </Row>
        <p className="sp-note-text">Note: Median sold price reflects the suburb as a whole and may not match the uniqueness of your property. For a more accurate valuation estimate for your property, click below.</p>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 40 }}>
          <Button as={Link} to={`/property-search-live/${agentDetails.landing_page_slug}`} className="sp-cta-btn" style={{ backgroundColor: agentDetails.agent_button_colour, borderColor: agentDetails.agent_button_colour }}>
            Free Property Valuation Tool
          </Button>
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <h2 className="sp-section-title" style={{ marginBottom: 0 }}>Suburb Property Type Summary</h2>
          <div style={{ minWidth: 200 }}>
            <Select
              options={propertyTypeRangeOptions}
              value={propertyTypeRange}
              onChange={(option) => setPropertyTypeRange(option)}
              isSearchable={false}
              styles={{
                menu: (provided) => ({ ...provided, zIndex: 9998 }),
                control: (provided) => ({ ...provided, borderRadius: 10, borderColor: '#e5e7eb', minHeight: 40 }),
              }}
            />
          </div>
        </div>
        <p className="sp-note-text">The data below summarises the results over the {propertyTypeRange.label.toLowerCase()} by property type and bedrooms.</p>
        <Tabs defaultActiveKey="sale" fill className="sp-tabs">
        <Tab eventKey="sale" title="Sale Market">
            {!soldSuburbPropertyTypeData || soldSuburbPropertyTypeData.length === 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: '2.5em' }}>
                <p style={{ textAlign: 'center', fontSize: '1em', color: '#9ca3af' }}>No sold properties in the {propertyTypeRange.label.toLowerCase()}</p>
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table className="sp-data-table" style={{ marginTop: '1.5em' }}>
                  <thead>
                    <tr>
                      <th>Property Type</th>
                      <th>Beds</th>
                      <th>Sold Price</th>
                      <th>Sold ({propertyTypeRange.label})</th>
                    </tr>
                  </thead>
                  <tbody>
                    {soldSuburbPropertyTypeData.map((property, index) => (
                      <tr key={index}>
                        <td>{property.property_type_display}</td>
                        <td>{property.bedrooms_string}</td>
                        <td>{property.display_price_short}</td>
                        <td>{property.properties_last_month}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Tab>
          <Tab eventKey="rent" title="Rental Market">
            {!rentSuburbPropertyTypeData || rentSuburbPropertyTypeData.length === 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: '2.5em' }}>
                <p style={{ textAlign: 'center', fontSize: '1em', color: '#9ca3af' }}>No rented properties in the {propertyTypeRange.label.toLowerCase()}</p>
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table className="sp-data-table" style={{ marginTop: '1.5em' }}>
                  <thead>
                    <tr>
                      <th>Property Type</th>
                      <th>Beds</th>
                      <th>Rented Price</th>
                      <th>Rented ({propertyTypeRange.label})</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rentSuburbPropertyTypeData.map((property, index) => (
                      <tr key={index}>
                        <td>{property.property_type_display}</td>
                        <td>{property.bedrooms_string}</td>
                        <td>{property.display_price_short}</td>
                        <td>{property.properties_last_month}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Tab>
        </Tabs>
        <h2 className="sp-section-title" style={{ marginTop: 40 }}>Median Prices Over Time</h2>
        {suburbMonthlyData && agentDetails && (
          <Row className="justify-content-md-center">
            <Col>
              <div style={{ overflowX: 'auto' }}>
                <Plot
                  data={formatDataForPlotly(suburbMonthlyData.data, 'median_sold_price_filled')}
                  layout={{
                    xaxis: {
                      title: 'Month',
                      tickformat: '%Y-%m',
                      type: 'date',
                    },
                    yaxis: {
                      title: 'Median Sold Price ($)',
                      tickprefix: '$',
                      range: [0, Math.max(...suburbMonthlyData?.data?.flatMap(d => d.median_sold_price_filled) || [0])],
                    },
                    autosize: true,
                  }}
                  config={{ displayModeBar: false }}
                  useResizeHandler={true}
                  style={{ width: '100%' }}
                />
              </div>
            </Col>
          </Row>
        )}
        <h2 className="sp-section-title" style={{ marginTop: 40 }}>Median Days on Market Over Time</h2>
        {suburbMonthlyData && agentDetails && (
          <Row className="justify-content-md-center">
            <Col>
              <div style={{ overflowX: 'auto' }}>
                <Plot
                  data={formatDataForPlotly(suburbMonthlyData.data, 'median_sold_days_on_market_filled')}
                  layout={{
                    xaxis: {
                      title: 'Month',
                      tickformat: '%Y-%m',
                      type: 'date',
                    },
                    yaxis: {
                      title: 'Median Days on Market',
                      range: [0, Math.max(...suburbMonthlyData?.data?.flatMap(d => d.median_sold_days_on_market_filled) || [0])],
                    },
                    autosize: true,
                  }}
                  config={{ displayModeBar: false }}
                  useResizeHandler={true}
                  style={{ width: '100%' }}
                />
              </div>
            </Col>
          </Row>
        )}
        <h2 className="sp-section-title" style={{ marginTop: 40 }}>Sold Properties Over Time</h2>
        {suburbMonthlyData && agentDetails && (
          <Row className="justify-content-md-center">
            <Col>
              <div style={{ overflowX: 'auto' }}>
                <Plot
                  data={formatDataForPlotlyBar(suburbMonthlyData.data, 'sold_properties')}
                  layout={{
                    barmode: 'stack',
                    xaxis: {
                      title: 'Month',
                      tickformat: '%Y-%m',
                      type: 'date',
                    },
                    yaxis: {
                      title: 'Sold Properties'
                    },
                    autosize: true,
                  }}
                  config={{ displayModeBar: false }}
                  useResizeHandler={true}
                  style={{ width: '100%' }}
                />
              </div>
            </Col>
          </Row>
        )}
        <div style={{ marginTop: 40, marginBottom: 30 }}>
          <h2 className="sp-section-title">Nearby Suburbs</h2>
          {loadingNearby || !nearbySuburbData ? (
            <div style={{ 
              position: 'relative', 
              minHeight: 120, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center' 
            }}>
              <Spinner animation="border" />
            </div>
          ) : (
            <SuburbsList suburbs={nearbySuburbData} agent_slug={agent_slug}/>
          )}
        </div>        
        <div>
          {suburbData && agentDetails && (
            <ComparableProperties
              suburb={suburbData.suburb_id}
              agentDetails={agentDetails}
              title="Market Activity"
              subtitle="Find out how your property compares to others in the area."
              modalContent={(
                <ol>
                  <li>Find a list of sold or rented properties using the filters provided. Try to find at least 5 if you can.</li>
                  <li>Exclude properties which are too different from your property.</li>
                  <li>The median price, max price, and min price of your final list is then calculated.</li>
                  <li>This would be an estimate of the price range for your property.</li>
                </ol>
              )}
            />
          )}
        </div>
      </Container>
    </div>
  );
};

export default SuburbProfile;